import type { FetchLike } from "@emdur/ui";
import contractedOperations from "./agentOperations.generated";
import type { CapabilityItem } from "./clients";
import { permissionPhrase, permissionTitle } from "./permissionCopy";
import { readVersioned } from "./fetcher";
import type { Role } from "./roles";

export interface AgentCreate {
  name: string;
  kind: "external" | "twin";
  role: string;
  capabilities: string[];
  autonomy?: Record<string, number>;
  skill?: string;
  sponsors: string[];
  disclosure?: {
    intended_purpose: string;
    limitations: string;
    precluded_uses?: string;
  };
  client_id?: string;
  jwks?: { keys: Record<string, unknown>[] };
  jwks_uri?: string;
  expires_at: string;
}
export interface Agent extends Omit<AgentCreate, "kind" | "jwks" | "jwks_uri"> {
  id: string;
  organization_id: string;
  kind: "external" | "twin" | "platform";
  autonomy: Record<string, number>;
  state: "active" | "suspended" | "expired" | "disabled";
  created_at: string;
  updated_at: string;
}
export const AGENT_KIND = {
  external: "External agent",
  twin: "AI twin",
  platform: "Platform agent",
};
/** Role envelopes can widen even when the agent keeps exactly the same capabilities. */
export function broaderRole(before: Role, after: Role): boolean {
  if (before.all_capabilities) return false;
  return (
    !!after.all_capabilities ||
    after.capabilities.some(
      (capability) => !before.capabilities.includes(capability),
    )
  );
}
export function outsideRole(agent: Agent, role: Role): string[] {
  return role.all_capabilities
    ? []
    : agent.capabilities.filter(
        (capability) => !role.capabilities.includes(capability),
      );
}
export const agentsPath = (org: string) =>
  `/v1/organizations/${encodeURIComponent(org)}/agents`;
export const agentPath = (org: string, agent: string) =>
  `${agentsPath(org)}/${encodeURIComponent(agent)}`;
export const agentHref = (org?: string, agent?: string) =>
  org
    ? `/orgs/${encodeURIComponent(org)}/agents${agent ? `/${encodeURIComponent(agent)}` : ""}`
    : "/agents";
const operations: Record<
  string,
  {
    capability: string;
    title: string;
    method: string;
    ceiling: number | string;
    narrowing?: number;
  }
> = contractedOperations;

export function agentExample(personId: string, now = Date.now()): AgentCreate {
  return {
    name: "Product releases",
    kind: "external",
    role: "admin",
    capabilities: [
      "developer.clients:read",
      "developer.clients:update",
      "agents.proposals:read",
    ],
    autonomy: { updateClient: 1 },
    sponsors: [personId],
    jwks_uri: "https://product.example.com/.well-known/jwks.json",
    expires_at: new Date(now + 30 * 86400000).toISOString(),
    disclosure: {
      intended_purpose:
        "Keep the product's app registration ready for each release.",
      limitations: "App changes need your approval.",
    },
  };
}
export const autonomyWords = (level: number) =>
  [
    "Suggests",
    "Drafts and asks you",
    "Acts and notifies you",
    "Acts without notifying you",
  ][level] ?? "Autonomy unavailable";
export function agentPermissionPhrase(
  agent: AgentCreate | Agent,
  catalogue: CapabilityItem[],
) {
  return new Intl.ListFormat("en", {
    style: "long",
    type: "conjunction",
  }).format(
    agent.capabilities.map((capability) => {
      const title = permissionPhrase([capability], catalogue);
      const changes =
        catalogue
          .find((item) => item.capability === capability)
          ?.operations.filter(
            (id) =>
              operations[id]?.method !== "get" &&
              typeof operations[id]?.ceiling === "number",
          ) ?? [];
      const levels = [
        ...new Set(changes.map((id) => agent.autonomy?.[id] ?? 0)),
      ];
      if (!levels.length) return title;
      return `${title} (${levels
        .map((level) =>
          level >= 2 &&
          changes.some(
            (id) =>
              (agent.autonomy?.[id] ?? 0) > Number(operations[id]?.ceiling),
          )
            ? "acting when reducing access; asking you first for new access"
            : [
                "suggesting only",
                "asking you first",
                "acting and notifying you",
                "acting without notifying you",
              ][level],
        )
        .join("; ")})`;
    }),
  );
}
export function agentAutonomySummary(
  agent: AgentCreate | Agent,
  catalogue: CapabilityItem[],
) {
  if (
    agent.capabilities.some(
      (capability) => !catalogue.some((item) => item.capability === capability),
    )
  )
    return "Autonomy unavailable";
  const changes = catalogue
    .filter((item) => agent.capabilities.includes(item.capability))
    .flatMap((item) => item.operations)
    .filter(
      (id) =>
        operations[id]?.method !== "get" &&
        typeof operations[id]?.ceiling === "number",
    );
  if (!changes.length) return "Reads only";
  const levels = [...new Set(changes.map((id) => agent.autonomy?.[id] ?? 0))];
  return levels
    .map((level) =>
      level === 1 &&
      changes.includes("updateClient") &&
      agent.autonomy?.updateClient === 1
        ? "Asks before changing an app"
        : ([
            "Suggests changes",
            "Asks before making changes",
            "Acts and notifies you",
            "Acts without notifying you",
          ][level] ?? "Autonomy unavailable"),
    )
    .join("; ");
}
export function agentOperationLines(
  agent: AgentCreate | Agent,
  catalogue: CapabilityItem[],
  capability: string,
) {
  return (
    catalogue.find((item) => item.capability === capability)?.operations ?? []
  )
    .filter((id) => typeof operations[id]?.ceiling === "number")
    .map((id) => ({
      id,
      title: permissionTitle(operations[id]!.title),
      level: agent.autonomy?.[id] ?? 0,
      words: autonomyWords(agent.autonomy?.[id] ?? 0),
      read: operations[id]?.method === "get",
      narrowingOnly:
        (agent.autonomy?.[id] ?? 0) > Number(operations[id]?.ceiling),
    }));
}
export const agentExpiry = (expiry: string) =>
  new Date(expiry).toLocaleDateString("en", { dateStyle: "long" });
export async function readAgent(fetch: FetchLike, org: string, id: string) {
  return readVersioned<Agent>(fetch, agentPath(org, id), "agent");
}
